/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { faChevronDoubleRight, faSlidersH, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { EmployeeModel } from '../../../models/employee.model';
import { TaskModel } from '../../../models/task.model';
import { WorkflowModel } from '../../../models/workflow.model';
import { EmployeeService } from '../../../services/employee.service';
import { TaskService } from '../../../services/task.service';
import { WorkflowService } from '../../../services/workflow.service';

interface TimeTrackingFilter {
  fromDate: Date;
  fromTime: string;
  toDate: Date;
  toTime: string;
  workflowId: string;
  employeeId: string;
  taskId: string;
}

@Component({
  selector: 'app-time-tracking',
  templateUrl: './time-tracking.component.html',
  styleUrls: ['./time-tracking.component.scss'],
  standalone: false
})
export class TimeTrackingComponent implements OnInit {
  // filter properties
  filterOpen = true;

  readonly faChevronDoubleRight = faChevronDoubleRight;
  readonly faSlidersH = faSlidersH;
  readonly faTimes = faTimes;

  readonly defaultFromDate = new Date(new Date().setUTCDate(new Date().getUTCDate() - 7));
  readonly defaultFromTime = '00:00';
  readonly defaultToDate = new Date();
  readonly defaultToTime = '23:59';

  filter: TimeTrackingFilter = {
    fromDate: this.defaultFromDate,
    fromTime: this.defaultFromTime,
    toDate: this.defaultToDate,
    toTime: this.defaultToTime,
    workflowId: '',
    employeeId: '',
    taskId: ''
  };

  workflows: WorkflowModel[] = [];
  filteredWorkflows: WorkflowModel[] = [];
  employees: EmployeeModel[] = [];
  tasks: TaskModel[] = [];
  selectedTasks: TaskModel[] = [];

  workflowsChangedSubscription: Subscription = new Subscription();
  employeesChangedSubscription: Subscription = new Subscription();
  tasksChangedSubscription: Subscription = new Subscription();
  selectedTasksSubscription: Subscription = new Subscription();

  @ViewChild(CdkVirtualScrollViewport)
  viewPort!: CdkVirtualScrollViewport;

  allTasks: TaskModel[] = [];
  allEmployees: EmployeeModel[] = [];

  today!: Date;

  workflowIdFromUrl: string = '';
  employeeIdFromUrl: string = '';
  taskIdFromUrl: string = '';
  fromDateFromUrl!: Date;
  toDateFromUrl!: Date;
  fromTimeFromUrl: string = '';
  toTimeFromUrl: string = '';
  doReset: boolean = false;

  constructor(
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private taskService: TaskService,
    private router: Router,
    private route: ActivatedRoute
  ) {}
  ngOnDestroy(): void {
    this.workflowsChangedSubscription.unsubscribe();
    this.employeesChangedSubscription.unsubscribe();
    this.tasksChangedSubscription.unsubscribe();
    this.selectedTasksSubscription.unsubscribe();
  }
  ngOnInit() {
    this.workflows = [];
    this.allEmployees = [];
    this.allTasks = [];
    this.selectedTasks = [];
    this.today = new Date();
    this.workflowService.searchWorkflow({
      search_criteria: { is_deleted: { $not: { $eq: true } } },
      limit: 0,
      skip: 0,
      sort: '-updatedAt',
      select: '_id name'
    });
    this.employeeService._searchEmployees({
      search_criteria: {},
      sort: 'full_name',
      select: '_id full_name',
      skip: 0,
      limit: 0
    });
    this.getDataFromURL();
    this.populateFilterTimeAndDate();
    this.filterDropdowns();
    this.selectedTasksSubscription = this.taskService.tasksSelected.subscribe(async (tasks) => {
      this.selectedTasks = tasks;
      this.workflows = await this.workflowService.searchWorkflow({
        search_criteria: { is_deleted: { $not: { $eq: true } } },
        limit: 0,
        skip: 0,
        sort: '-updatedAt',
        select: '_id name'
      });
      if (this.workflows.length <= 0) {
        this.resetFilter();
      } else {
        this.filteredWorkflows = this.workflows.filter((workflow) =>
          this.selectedTasks.map((task) => task.workflowId).includes(workflow._id)
        );
        this.doReset = false;
        if (
          this.filter.workflowId &&
          !this.filteredWorkflows.map((workflow) => workflow._id).includes(this.filter.workflowId)
        ) {
          this.doReset = true;
        }
        if (
          this.filter.taskId &&
          !this.selectedTasks.map((task) => task.taskId).includes(this.filter.taskId)
        ) {
          this.doReset = true;
        }
        if (this.filteredWorkflows.length <= 0 || this.doReset) {
          this.resetFilter();
        }

        this.employeeService
          ._searchEmployees({
            search_criteria: {},
            sort: 'full_name',
            select: '_id full_name',
            skip: 0,
            limit: 0
          })
          .then((employees) => {
            this.allEmployees = employees;

            if (this.workflowIdFromUrl) {
              this.filter.workflowId = this.workflowIdFromUrl;
              this.filterEmployees();
              if (this.employeeIdFromUrl) {
                this.filter.employeeId = this.employeeIdFromUrl;
                this.filterTasks();
                if (this.taskIdFromUrl) {
                  this.filter.taskId = this.taskIdFromUrl;
                }
              }
              // to enter this condition only once, to not assign the fields to path parameters more than once.
              this.workflowIdFromUrl = '';
              this.employeeIdFromUrl = '';
              this.taskIdFromUrl = '';
            }
          });
      }
    });

    // this.workflowService
    //   .searchWorkflow({
    //     search_criteria: { is_deleted: { $not: { $eq: true } } },
    //     limit: 0,
    //     skip: 0,
    //     sort: '-updatedAt',
    //     select: '_id name'
    //   })
    //   .then((workflows) => {
    //     this.workflows = workflows;

    //     if (this.workflows.length <= 0) {
    //       this.resetFilter();
    //     }
    //   });

    let filterStart = this.getIsoStringFromDateAndTimeString(
      this.filter.fromDate,
      this.filter.fromTime
    );
    let filterEnd = this.getIsoStringFromDateAndTimeString(this.filter.toDate, this.filter.toTime);

    this.taskService.selectTasks(
      {
        search_criteria: {},
        skip: 0,
        limit: 0,
        sort: 'updatedAt',
        select: 'task_id name assigned_to workflow_id executed_by claimed_by time_tracking'
      },
      filterStart,
      filterEnd
    );

    //this method used for calling the search services
    this.filterDropdowns();
  }

  resetFilter() {
    this.filter.workflowId = '';
    this.filter.employeeId = '';
    this.filter.taskId = '';
    this.employees = [];
    this.tasks = [];
    this.onFilterClicked();
  }

  populateFilterTimeAndDate() {
    this.filter.fromDate = this.fromDateFromUrl ? this.fromDateFromUrl : this.defaultFromDate;
    this.filter.toDate = this.toDateFromUrl ? this.toDateFromUrl : this.defaultToDate;
    this.filter.fromTime = this.fromTimeFromUrl ? this.fromTimeFromUrl : this.defaultFromTime;
    this.filter.toTime = this.toTimeFromUrl ? this.toTimeFromUrl : this.defaultToTime;
  }

  onFilterClicked(): void {
    if (this.filter.taskId) {
      this.navigateToTaskDashboard(
        this.filter.workflowId,
        this.filter.employeeId,
        this.filter.taskId
      );
    } else if (this.filter.employeeId) {
      this.navigateToEmployeeDashboard(this.filter.workflowId, this.filter.employeeId);
    } else if (this.filter.workflowId) {
      this.navigateToWorkflowDashboard(this.filter.workflowId);
    } else this.router.navigate(['home/time-tracking']);
  }

  // Updates a date object with a string representation of time
  getIsoStringFromDateAndTimeString(date: Date, time: string): string {
    let timeParts = time.split(':');
    let hours = parseInt(timeParts[0]);
    let minutes = parseInt(timeParts[1]);

    date.setHours(hours);
    date.setMinutes(minutes);
    date.setSeconds(0);
    date.setMilliseconds(0);

    return date.toISOString();
  }

  navigateToWorkflowDashboard(workflowId: string) {
    let url: string =
      'home/time-tracking/workflow/' +
      workflowId +
      '/' +
      this.filter.fromDate.toISOString() +
      '/' +
      this.filter.toDate.toISOString() +
      '/' +
      this.filter.fromTime +
      '/' +
      this.filter.toTime;

    this.router.navigateByUrl(url);
  }

  navigateToEmployeeDashboard(workflowId: string, employeeId: string) {
    let url: string =
      'home/time-tracking/workflow/' +
      workflowId +
      '/employee/' +
      employeeId +
      '/' +
      this.filter.fromDate.toISOString() +
      '/' +
      this.filter.toDate.toISOString() +
      '/' +
      this.filter.fromTime +
      '/' +
      this.filter.toTime;
    this.router.navigateByUrl(url);
  }

  navigateToTaskDashboard(workflowId: string, employeeId: string, taskId: string) {
    let url: string =
      'home/time-tracking/workflow/' +
      workflowId +
      '/employee/' +
      employeeId +
      '/task/' +
      taskId +
      '/' +
      this.filter.fromDate.toISOString() +
      '/' +
      this.filter.toDate.toISOString() +
      '/' +
      this.filter.fromTime +
      '/' +
      this.filter.toTime;
    this.router.navigateByUrl(url);
  }

  // getSearchCriteria(): {} {
  //   let criteria = {};
  //   let andBlock = [];
  //   andBlock.push({ status: { $not: { $eq: 'DELETED' } } });
  //   andBlock.push({
  //     createdAt: {
  //       $gte: this.getIsoStringFromDateAndTimeString(this.filter.fromDate, this.filter.fromTime)
  //     }
  //   });
  //   andBlock.push({
  //     createdAt: {
  //       $lte: this.getIsoStringFromDateAndTimeString(this.filter.toDate, this.filter.toTime)
  //     }
  //   });
  //   criteria = { $and: andBlock };

  //   return criteria;
  // }

  filterEmployees() {
    this.filter.employeeId = '';
    this.filter.taskId = '';
    this.tasks = [];
    this.employees = [];
    let emps: string[] = [];

    // this will get only the tasks for this workflow
    let workflowTasks = this.selectedTasks.filter(
      (task: TaskModel) => task.workflowId == this.filter.workflowId
    );

    //this will get the (id)s for all the employees that have done any of this workflow tasks
    workflowTasks.forEach((task: TaskModel) => {
      task.executedBy.forEach((id: string) => {
        emps.push(id);
      });
    });

    //this will get the data of the employees
    this.allEmployees.forEach((employee) => {
      if (emps.includes(employee.employeeId) == true) {
        this.employees.push(employee);
      }
    });
  }
  filterTasks() {
    this.filter.taskId = '';
    this.tasks = [];

    // this will get only the tasks for this workflow and this employee,
    // by getting the tasks based on workflow id and employee id.
    let employeeTasks = this.selectedTasks.filter(
      (task) =>
        task.workflowId == this.filter.workflowId &&
        task.executedBy.includes(this.filter.employeeId)
    );

    //this will set the task data in a key value form.
    employeeTasks.forEach((task) => {
      if (task.time_tracking && task.time_tracking.length >= 2) {
        this.tasks.push(task);
      }
    });
  }
  filterDropdowns() {
    // this.resetFilter();
    let filterStart = this.getIsoStringFromDateAndTimeString(
      this.filter.fromDate,
      this.filter.fromTime
    );
    let filterEnd = this.getIsoStringFromDateAndTimeString(this.filter.toDate, this.filter.toTime);
    this.taskService.selectTasks(
      {
        search_criteria: {
          status: { $eq: 'CLOSED' }
        },
        skip: 0,
        limit: 0,
        sort: 'updatedAt',
        select: 'task_id name assigned_to workflow_id executed_by claimed_by time_tracking'
      },
      filterStart,
      filterEnd
    );
  }
  getDataFromURL() {
    if (this.route.children[0]) {
      this.route.children[0].params.subscribe((params: Params) => {
        if (params) {
          this.fromDateFromUrl = new Date(params.fromDate);
          this.toDateFromUrl = new Date(params.toDate);
          this.fromTimeFromUrl = params.fromTime;
          this.toTimeFromUrl = params.toTime;
          this.workflowIdFromUrl = params.workflowId;
          if (params.employeeId) {
            this.employeeIdFromUrl = params.employeeId;
          }
          if (params.taskId) {
            this.taskIdFromUrl = params.taskId;
          }
        }
      });
    }
  }
}
